import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import { calculateWorkTotals, daysInMonth, type DayRecord, type Profile } from "../shared/domain";

const NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const REL_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const PACKAGE_REL_NS = "http://schemas.openxmlformats.org/package/2006/relationships";
const VT_NS = "http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes";
const XML_NS = "http://www.w3.org/XML/1998/namespace";

function parseXml(bytes: Uint8Array): XMLDocument {
  const document = new DOMParser().parseFromString(strFromU8(bytes), "application/xml");
  const error = document.querySelector("parsererror");
  if (error) throw new Error("ExcelテンプレートのXMLを読み込めませんでした。");
  return document;
}

function serializeXml(document: XMLDocument): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(strToU8(new XMLSerializer().serializeToString(document)));
}

function cell(document: XMLDocument, reference: string): Element {
  const found = Array.from(document.getElementsByTagNameNS(NS, "c")).find((node) => node.getAttribute("r") === reference);
  if (!found) throw new Error(`Excelセル ${reference} が見つかりません。`);
  return found;
}

function clearCell(target: Element): void {
  while (target.firstChild) target.removeChild(target.firstChild);
  target.removeAttribute("t");
}

function setNumber(document: XMLDocument, reference: string, value: number | null): void {
  const target = cell(document, reference);
  clearCell(target);
  if (value === null) return;
  const node = document.createElementNS(NS, "v");
  node.textContent = String(value);
  target.append(node);
}

function setText(document: XMLDocument, reference: string, value: string): void {
  const target = cell(document, reference);
  clearCell(target);
  target.setAttribute("t", "inlineStr");
  const inline = document.createElementNS(NS, "is");
  const text = document.createElementNS(NS, "t");
  if (value.startsWith(" ") || value.endsWith(" ")) text.setAttributeNS(XML_NS, "xml:space", "preserve");
  text.textContent = value;
  inline.append(text);
  target.append(inline);
}

function excelSerial(year: number, month: number, day: number): number {
  return Math.round((Date.UTC(year, month - 1, day) - Date.UTC(1899, 11, 30)) / 86_400_000);
}

function statusText(day: DayRecord, includeSchedule: boolean): string {
  if (day.status === "day_off") return "公休";
  if (day.status === "paid_leave") return "有給";
  if (day.status === "holiday_work") {
    return includeSchedule && day.scheduleText ? `${day.scheduleText}、休日出勤` : "休日出勤";
  }
  return includeSchedule ? day.scheduleText : "";
}

export async function createMonthlyWorkbook(options: {
  monthKey: string;
  profile: Profile;
  days: DayRecord[];
  includeSchedule: boolean;
}): Promise<Blob> {
  const response = await fetch("/templates/worklog-template.xlsx", { cache: "no-store" });
  if (!response.ok) throw new Error("Excelテンプレートを取得できませんでした。");
  const archive = unzipSync(new Uint8Array(await response.arrayBuffer()));
  const workbook = parseXml(archive["xl/workbook.xml"]);
  const relationships = parseXml(archive["xl/_rels/workbook.xml.rels"]);
  const sheetNode = workbook.getElementsByTagNameNS(NS, "sheet")[0];
  const relationshipId = sheetNode.getAttributeNS(REL_NS, "id");
  const relationship = Array.from(relationships.getElementsByTagNameNS(PACKAGE_REL_NS, "Relationship"))
    .find((node) => node.getAttribute("Id") === relationshipId);
  if (!relationship) throw new Error("Excelシートの関連情報が見つかりません。");
  const sheetPath = `xl/${relationship.getAttribute("Target")?.replace(/^\//u, "")}`;
  const sheet = parseXml(archive[sheetPath]);

  const [year, month] = options.monthKey.split("-").map(Number);
  const count = daysInMonth(options.monthKey);
  const sheetName = `${year}年${month}月`;
  sheetNode.setAttribute("name", sheetName);
  setText(sheet, "H1", `${year}年　${month}月分　（　${month}/1　～　${month}/${count}）`);
  setText(sheet, "A3", ` 職場名 ${options.profile.workplace}`);
  setText(sheet, "C5", options.profile.displayName);

  const byDate = new Map(options.days.map((day) => [day.workDate, day]));
  let totalWorked = 0;
  let totalOvertime = 0;

  for (let index = 0; index < 31; index += 1) {
    const row = index + 10;
    const dayNumber = index + 1;
    const valid = dayNumber <= count;
    setNumber(sheet, `A${row}`, valid ? (index === 0 ? excelSerial(year, month, dayNumber) : dayNumber) : null);
    for (const column of ["B", "C", "D", "E", "F", "G", "H"]) setNumber(sheet, `${column}${row}`, null);
    setText(sheet, `I${row}`, "");
    if (!valid) continue;

    const date = `${options.monthKey}-${String(dayNumber).padStart(2, "0")}`;
    const day = byDate.get(date);
    if (!day) continue;
    setText(sheet, `I${row}`, statusText(day, options.includeSchedule));
    if ((day.status !== "work" && day.status !== "holiday_work") || !day.actualConfirmed) continue;

    const totals = calculateWorkTotals(day);
    setNumber(sheet, `B${row}`, day.actualStartMinutes === null ? null : day.actualStartMinutes / 1440);
    setNumber(sheet, `C${row}`, day.actualEndMinutes === null ? null : day.actualEndMinutes / 1440);
    setNumber(sheet, `F${row}`, day.breakMinutes / 60);
    setNumber(sheet, `G${row}`, totals.workedMinutes / 60);
    setNumber(sheet, `H${row}`, totals.overtimeMinutes / 60);
    totalWorked += totals.workedMinutes;
    totalOvertime += totals.overtimeMinutes;
  }

  setNumber(sheet, "G41", totalWorked / 60);
  setNumber(sheet, "H41", totalOvertime / 60);
  archive["xl/workbook.xml"] = serializeXml(workbook);
  archive[sheetPath] = serializeXml(sheet);

  const appPath = "docProps/app.xml";
  if (archive[appPath]) {
    const app = parseXml(archive[appPath]);
    const titles = Array.from(app.getElementsByTagNameNS(VT_NS, "lpstr"));
    if (titles.length) titles[titles.length - 1].textContent = sheetName;
    archive[appPath] = serializeXml(app);
  }
  const corePath = "docProps/core.xml";
  if (archive[corePath]) {
    const core = parseXml(archive[corePath]);
    const modified = Array.from(core.getElementsByTagNameNS("http://purl.org/dc/terms/", "modified"))[0];
    if (modified) modified.textContent = new Date().toISOString();
    archive[corePath] = serializeXml(core);
  }

  return new Blob([zipSync(archive, { level: 6 })], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
